import { useEffect, useMemo, useRef } from 'react';
import isEqual from 'hs-lodash/isEqual';
import { FEATURES } from 'collaboration-sidebar/constants/features';
import usePrevious from 'collaboration-sidebar/hooks/usePrevious';
import { hasFeature } from '../utils/features';
import useGetPropsFromFAS from '../features/fas/hooks/useGetPropsFromFAS';

/**
 * Today, several prop values (such as objectId ,objectType, ctaUrl)
 * are duplicated in CollaborationSidebarState, where they can be read
 * by the mini commenter. To ensure these values are up to date, we
 * sync into the state from props.
 */
export default function usePersistPropsToCollaborationSidebarState({
  onUpdateCollaborationSidebarState,
  collaborationSidebarProps: {
    objectId,
    objectType,
    appName,
    enabledFeatures,
    ctaUrl,
    tasksConfig,
    skipFas
  }
}) {
  'use no memo';

  const {
    fasProps
  } = useGetPropsFromFAS({
    appName: appName || '',
    enabledFeatures,
    skipFas
  });
  const effectiveEnabledFeatures = skipFas ? enabledFeatures : fasProps === null || fasProps === void 0 ? void 0 : fasProps.enabledFeatures;
  const isEmbeddedCommentingEnabled = hasFeature(effectiveEnabledFeatures, FEATURES.DEEP_COMMENTING);
  const currentUrl = window.location.href;
  const resolvedObjectType = (fasProps === null || fasProps === void 0 ? void 0 : fasProps.objectType) || objectType;
  const updatesToPersist = useMemo(() => Object.assign({
    isEmbeddedCommentingEnabled,
    // These values may be undefined while FAS is loading. Coerce to null
    // so the state stays valid against the schema (which expects string | null).
    appName: appName || null
  }, objectId ? {
    objectId
  } : {}, resolvedObjectType ? {
    objectType: resolvedObjectType
  } : {}, {
    ctaUrl: ctaUrl || currentUrl,
    enabledFeatures: effectiveEnabledFeatures || [],
    tasksConfig: tasksConfig || {}
  }), [appName, ctaUrl, currentUrl, effectiveEnabledFeatures, isEmbeddedCommentingEnabled, objectId, resolvedObjectType, tasksConfig]);
  const prevUpdatesToPersist = usePrevious(updatesToPersist);
  const mounted = useRef(false);

  /**
   * Persist prop updates to state
   */
  useEffect(() => {
    if (!mounted.current || !isEqual(prevUpdatesToPersist, updatesToPersist)) {
      onUpdateCollaborationSidebarState(updatesToPersist);
    }
    mounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevUpdatesToPersist, updatesToPersist]);

  /**
   * As an optimization, return updates so they can be passed through context right away
   * Avoiding a single render where they haven't yet been set in the state
   */
  return updatesToPersist;
}